import { useEffect, useRef } from "react";

const ClusterManager = ({
  map,
  vehiculos,
  zoomActual,
  markersRef,
  seguimientoActivo,
  seguimientoMarkerRef,
  marcadoresListos // 👈 Recibe el gatillo de sincronización
}) => {

  const clusterMarkersRef = useRef({});

  const createClusterIcon = (count) => {
    const google = window.google;

    const size = count < 10 ? 44 : count < 50 ? 52 : 60;

    const svg = `
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="${size}"
        height="${size}"
        viewBox="0 0 ${size} ${size}"
      >
        <defs>
          <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#3b82f6" />
            <stop offset="100%" stop-color="#2563eb" />
          </linearGradient>
          <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="5" stdDeviation="5" flood-color="rgba(0,0,0,0.35)" />
          </filter>
        </defs>
        <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}" fill="url(#grad)" opacity="0.18" />
        <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 8}" fill="url(#grad)" filter="url(#shadow)" />
        <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 9}" fill="none" stroke="rgba(255,255,255,0.95)" stroke-width="2" />
        <text
          x="50%"
          y="54%"
          dominant-baseline="middle"
          text-anchor="middle"
          font-family="Inter, Arial"
          font-size="${size * 0.28}"
          font-weight="700"
          fill="#ffffff"
        >
          ${count}
        </text>
      </svg>
    `;

    return {
      url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
      scaledSize: new google.maps.Size(size, size),
      anchor: new google.maps.Point(size / 2, size / 2)
    };
  };

  useEffect(() => {
    if (!map) return;

    const google = window.google;

    // Limpiar clusters previos
    Object.values(clusterMarkersRef.current).forEach((marker) => {
      if (marker) marker.setMap(null);
    });
    clusterMarkersRef.current = {};

    // Mostrar todos los marcadores por defecto
    Object.values(markersRef.current).forEach((marker) => {
      if (marker) marker.setMap(map);
    });

    if (zoomActual >= 16) return;

    const usados = new Set();
    const threshold =
      zoomActual <= 10
        ? 0.08
        : zoomActual <= 12
        ? 0.03
        : zoomActual <= 14
        ? 0.01
        : 0.004;

    for (let i = 0; i < vehiculos.length; i++) {
      const v1 = vehiculos[i];
      if (!v1 || usados.has(v1.idVehiculo)) continue;

      if (
        seguimientoActivo &&
        seguimientoMarkerRef.current === markersRef.current[v1.idVehiculo]
      ) {
        continue;
      }

      const lat1 = Number(v1.latitude);
      const lng1 = Number(v1.longitude);
      if (isNaN(lat1) || isNaN(lng1)) continue;

      const grupo = [v1];
      usados.add(v1.idVehiculo);

      for (let j = i + 1; j < vehiculos.length; j++) {
        const v2 = vehiculos[j];
        if (!v2 || usados.has(v2.idVehiculo)) continue;

        const lat2 = Number(v2.latitude);
        const lng2 = Number(v2.longitude);
        if (isNaN(lat2) || isNaN(lng2)) continue;

        if (
          Math.abs(lat1 - lat2) < threshold &&
          Math.abs(lng1 - lng2) < threshold
        ) {
          grupo.push(v2);
          usados.add(v2.idVehiculo);
        }
      }

      if (grupo.length > 1) {
        grupo.forEach((v) => {
          const marker = markersRef.current[v.idVehiculo];
          if (marker) marker.setMap(null);
        });

        const avgLat = grupo.reduce((sum, v) => sum + Number(v.latitude), 0) / grupo.length;
        const avgLng = grupo.reduce((sum, v) => sum + Number(v.longitude), 0) / grupo.length;

        const clusterMarker = new google.maps.Marker({
          position: { lat: avgLat, lng: avgLng },
          map,
          icon: createClusterIcon(grupo.length),
          zIndex: 999
        });

        clusterMarker.addListener("mouseover", () => {
          clusterMarker.setIcon(createClusterIcon(grupo.length));
        });

        clusterMarker.addListener("mouseout", () => {
          clusterMarker.setIcon(createClusterIcon(grupo.length));
        });

        clusterMarker.addListener("click", () => {
          map.panTo({ lat: avgLat, lng: avgLng });
          map.setZoom(Math.min(zoomActual + 2, 18));
        });

        clusterMarkersRef.current[`cluster_${v1.idVehiculo}`] = clusterMarker;
      }
    }

    return () => {
      Object.values(clusterMarkersRef.current).forEach((marker) => {
        if (marker) marker.setMap(null);
      });
    };

  // 🔥 Se añade marcadoresListos a las dependencias. Esto fuerza la ejecución de inmediato 
  // cuando los marcadores terminan de procesarse.
  }, [map, vehiculos, zoomActual, seguimientoActivo, markersRef, seguimientoMarkerRef, marcadoresListos]);

  return null;
};

export default ClusterManager;